import React, { Component } from 'react';
import { withRouter } from 'react-router';
import classNames from 'classnames';
import GoogleMapReact from 'google-map-react';
import withFetch from '../../hoc/withFetch';
import withAuthProvider from '../../hoc/withAuthProvider';
import { generateFeatures } from './helpers/utils/convert-to-geojson';
import { featureCollectionToTurfPolygons } from './helpers/utils/createTurfPolygons';
import Overlay from './helpers/ui/overlay';
import SelectedFilters from './detail-components/selected-filters';
import FilteredChains from './detail-components/filtered-chains';
import Demographics from './detail-components/demographics';
import Population from './detail-components/population';
import PurchasingPower from './detail-components/purchasing-power';
import Origin from './detail-components/origin';
import Household from './detail-components/household';
import ClassificationsTable from './classifications/ClassificationsTable';
import DropdownButton from '../../components/dropdown-button';
import ExportFooter from './export-footer';
import { ButtonAnimation } from '../../components/export-btn';
import Spinner from '../../components/spinner';
import SpinnerDots from '../spinner/dots';
import CheckboxToggler from '../checkbox-toggler';
import { createHouseMarker } from './helpers/drawing/drawing-operations';
import Heading from '../heading';
import { getColoredPinCssString } from './helpers/utils/pin-color-utils';
import LocationHandler from './helpers/ui/location-handler';
import { GOOGLE_MAPS_API_KEY, REACT_APP_API_BASE} from '../../config';
import './details.scss';

const TogglePins = props => {
    return (
        <CheckboxToggler checked={props.checked} onChange={() => {
            props.onChange(!props.checked);
        }} label={props.label} />
    );
};
class Details extends Component {
    getClassificationTableColumns() {
        return [
            {
                key: 'name',
                label: 'Klassificering',
                isTextCell: true,
                isTextHeader: true,
                render: (row) => row.name
            },
            {
                key: 'numberOfUnits',
                label: 'Antal',
                render: (row) => row.numberOfUnits?.toLocaleString('sv')
            },
            {
                key: 'totalTurnover',
                label: 'Omsättning',
                sublabel: 'KKR',
                render: (row) => row.totalTurnover?.toLocaleString('sv')
            },
            {
                key: 'totalRetailArea',
                label: 'Säljyta',
                sublabel: 'm2',
                render: (row) => row.totalRetailArea?.toLocaleString('sv')
            },
            {
                key: 'frequency',
                label: 'Frekvens',
                sublabel: 'Besök/säljcykel',
                render: (row) => row.frequency?.toLocaleString('sv')
            },
            {
                key: 'visitingTime',
                label: 'Besökstid/säljcykel',
                sublabel: 'h',
                render: (row) => row.vistingTimeMinutes?.toLocaleString('sv')
            }
        ];
    }

    getClassificationTableFooter(rows = []) {
        const total = (key) => rows.reduce((sum, row) => sum + (row[key] || 0), 0);

        return [
            {
                content: 'Totalt',
                isText: true,
                classNames: { 'table__text': true }
            },
            {
                content: `${total('numberOfUnits').toLocaleString('sv')} st`
            },
            {
                content: `${total('totalTurnover').toLocaleString('sv')} KKR`
            },
            {
                content: `${total('totalRetailArea').toLocaleString('sv')} m2`
            },
            {
                content: `${total('frequency').toLocaleString('sv')} besök`
            },
            {
                content: `${total('vistingTimeMinutes').toLocaleString('sv')} h`
            }
        ];
    }

    map = null;;
    maps = null;
    salesDistricts = null;
    salesDistrictsOverlay = null;

    constructor(props) {
        super(props);

        this.state = {
            showPins: false,
            isExporting: false,
            details: null,
            pins: null,
            isLoading: true,
            mapIsLoading: true
        };
        this.dropDown = React.createRef();
    }

    async fetchDetails(id) {
        const res = await this.props.fetch(`${REACT_APP_API_BASE}/salesdistrict/report/${id}`);
        const district = await res.json();
        return {
            ...district,
            homeMarkerGeoJson: district.homeMarkerGeoJson && district.homeMarkerGeoJson !== '' ? JSON.parse(district.homeMarkerGeoJson) : [],
            spatialGeoJson: JSON.parse(district.spatialGeoJson)
        };
    }

    async fetchPins() {        
        const res = await this.props.fetch(`${REACT_APP_API_BASE}/salesdistrict/units/${this.props.type}`);
        return await res.json();
    }

    async toggleUnits() {
        let pins = this.state.pins;
        if (!this.state.showPins) {
            this.setState({
                ...this.state,
                showPins: true,
                mapIsLoading: true
            });
            
            if (!pins) {
                pins = await this.fetchPins();
                this.setState({ pins });
            }
            
            const districtId = this.state.details.id;
            this.map.data.forEach((feature) => {
                const fid = feature.getProperty('districtId');
                if (fid != null && String(fid) === String(districtId)) {
                    this.map.data.overrideStyle(feature, {
                        visible: false
                    });
                }
            });

            const districtUnitIds = new Set(this.state.details.units || []);
            const pinsInDistrict = [];
            const addedKeys = new Set();

            const addPin = (pin) => {
                if (!pin) return;
                const key = pin.delfiId || pin.companyId;
                if (!key || addedKeys.has(key)) {
                    return;
                }
                addedKeys.add(key);
                pinsInDistrict.push(pin);
            };

            pins.forEach(pin => {
                if (districtUnitIds.has(pin.companyId)) {
                    addPin(pin);
                }
            });

            const salesDistrictFilters = this.state.details.salesDistrictFilters;
            if (salesDistrictFilters && salesDistrictFilters.unitsIncludeExclude && salesDistrictFilters.unitsIncludeExclude.length > 0) {
                const includedUnits = salesDistrictFilters.unitsIncludeExclude.filter(inc => inc.include);
                if (includedUnits.length > 0) {
                    includedUnits.forEach(includeItem => {
                        const pin = pins.find(candidate => {
                            if (includeItem.delfiId && candidate.delfiId === includeItem.delfiId) {
                                return true;
                            }
                            if (includeItem.companyId && candidate.companyId === includeItem.companyId) {
                                return true;
                            }
                            return false;
                        });
                        addPin(pin);
                    });
                }
            }

            LocationHandler.removeMarkersWithoutClusters();
            const pinCss = getColoredPinCssString(this.state.details.color);
            
            await LocationHandler.createLocations({
                map: this.map,
                maps: this.maps,
                units: pinsInDistrict,
                disableFitBounds: true,
                onClick: async ({ units }) => {},
                onLoadingComplete: () => {},
                useClusters: false,
                markerStyle: pinCss
            });
            
            LocationHandler.fitBounds(this.map, this.maps);
        } else {
            LocationHandler.removeMarkersWithoutClusters();
            const districtId = this.state.details.id;
            this.map.data.forEach((feature) => {
                const fid = feature.getProperty('districtId');
                if (fid != null && String(fid) === String(districtId)) {
                    this.map.data.overrideStyle(feature, {
                        visible: true
                    });
                }
            });

            this.setState({
                ...this.state,
                showPins: false,
                mapIsLoading: false
            });
        }
    }

    async componentDidUpdate() {
        if (!this.props.isAuthenticated) return;

        const { match } = this.props;
        
        if (!this.state.details) {
            const details = await this.fetchDetails(match.params.id);
            this.setState({
                details,
                isLoading: false
            });
        }
    }

    districtArea() {
        if (!this.state.details) return '';

        return `${this.state.details.salesDistrictArea.toLocaleString('sv')} km2`;
    }

    renderSaleDistrict() {
        const { details } = this.state;

        if (!details.spatialGeoJson || !details.spatialGeoJson[0]) return;
        
        const features = [];
        details.spatialGeoJson.forEach((districtFeature, index) => {
            const generated = generateFeatures(
                `district-${details.id}-${index}`,
                districtFeature,
                'salesdistrict',
                details.color ?? districtFeature.properties?.color,
                details.id
            );
            if (generated) {
                features.push(generated);
            }
        });

        if (features.length === 0) {
            return;
        }

        this.salesDistricts = {
            type: 'FeatureCollection',
            features
        };

        this.map.data.addGeoJson(this.salesDistricts);
        this.map.data.setStyle({ 
            strokeWeight: 1.5, 
            fillColor: details.spatialGeoJson[0].properties.color,
            fillOpacity: 0.5, 
            strokeColor: details.spatialGeoJson[0].properties.color,
            visible: true,
            clickable: false
        });


        this.renderHomeMarker();
        
        this.zoomToDistrict();
    }

    renderHomeMarker() {
        const { details } = this.state;
        if (!details.homeMarkerGeoJson) return;
        if (details.homeMarkerGeoJson.length === 0) return;

        const markerCoordinates = details.homeMarkerGeoJson[0].geometry.coordinates;
        const center = new this.maps.LatLng(markerCoordinates[1], markerCoordinates[0]);
        this.houseMarker = createHouseMarker({
            maps: this.maps,
            map: this.map,
            center,
            draggable: false,
            showCloseButton: false,
            style: {
                backgroundColor: details.color
            }
        });
    }

    zoomToDistrict() {
        const bounds = new this.maps.LatLngBounds()
    
        this.map.data.forEach((shape) => {
            if (shape.getGeometry().getType() === 'GeometryCollection') {
                shape.getGeometry().getArray().forEach(inner => {
                    inner.forEachLatLng(latlng => {
                            bounds.extend(latlng);
                    });                    
                });
            } else {
                shape.getGeometry().forEachLatLng(function(latlng){
                    bounds.extend(latlng);
                });
            }
        });
    
        this.map.fitBounds(bounds);
      
    }

    createLabelOverlay() {
        if (!this.salesDistricts) return;

        let center = null;
        let modifiers = {
            'overlay-label--districts': true
        };
        const { details } = this.state;
       
        if (details.homeMarkerGeoJson && details.homeMarkerGeoJson.length > 0) {
            center = details.homeMarkerGeoJson;
            modifiers = {
                'overlay-label--districts': true,
                'overlay-label--is-label-home': true
            };
        }

        this.salesDistrictsOverlay = Overlay({ 
            polygons: featureCollectionToTurfPolygons(this.salesDistricts, center), 
            map: this.map, 
            maps: this.maps,
            ignoreArea: true,
            modifiers: modifiers
        });
    }

    handleApiLoaded(map, maps) {
        this.map = map;
        this.maps = maps;
        this.renderSaleDistrict(map, maps);
        

        this.maps.event.addListener(this.map, 'zoom_changed', () => {
            if (this.salesDistrictsOverlay) {
                this.salesDistrictsOverlay.setMap(null);
            }
        });
        this.maps.event.addListener(this.map, 'idle', () => {
            if (this.salesDistrictsOverlay) {
                this.salesDistrictsOverlay.setMap(null);
            }

            this.createLabelOverlay();

            setTimeout(() => {
                this.setState({
                    mapIsLoading: false
                });
            }, 1000);
        });
    }

    getMimeType(format) {
        if (format === 'excel') {
            return 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
        }

        return 'application/xml';
    }

    async export(format) {
        this.setState({
            isExporting: true
        });

        this.dropDown.current.hideDropDown();

        const { match } = this.props;

        const res = await this.props.fetch(`${REACT_APP_API_BASE}/salesdistrict/export/${format}/${match.params.id}`);
        
        if (res.ok) {
            const exportReport = await res.blob();
            var blob = new Blob([exportReport], { type: this.getMimeType(format) });
            var link = document.createElement('a');
            link.href = window.URL.createObjectURL(blob);
            link.download = format === 'xml' ? `${this.state.details.name}.xml` : `${this.state.details.name}.xlsx`;
            link.click();

            this.setState({
                isExporting: false,
                isExportingFinished: true
            });

            setTimeout(() => {
                this.setState({
                    isExportingFinished: false
                });
            }, 4000);
        }
    }

    getExportStateText() {
        if (this.state.isExporting) {
            return 'Exporterar';
        }

        if (this.state.isExportingFinished) {
            return 'Klar!';
        }

        return 'Exportera';
    }
    
    render() {
        const { details } = this.state;
        const { match } = this.props;

        const center = {
            lat: 59.3273056, lng: 17.8390038
        };
       

        const preSlug = this.props.type === 'foodservice' ? `/foodservice-scenario/${match.params.scenarioId}` : `/scenario/${match.params.scenarioId}`;

        return (
            <section className={classNames({
                'details': true,
                'details--is-loading': this.state.isLoading
            })}>
                <div className="details__wrapper">
                    <Heading heading={details ? details.name : ''}
                             subheading={this.districtArea()}
                             breadcrumb="Alla distrikt"
                             breadcrumbUrl={preSlug}>
                        <div className="details__edit">
                            <a className="details__edit-btn" href={`${preSlug}/create/${match.params.id}`}>Ändra distrikt</a>
                        </div>
                    </Heading>

                    {details &&
                        <>
                            <SelectedFilters {...details} />                    
                            <div className="details__toggle-pins">
                                <TogglePins 
                                    checked={this.state.showPins} 
                                    label="Visa enheter på karta"
                                    onChange={() => {
                                        this.toggleUnits();
                                    }} />
                            </div>
                            <div className={classNames({
                                'districts__map': true,
                                'districts__map--is-loading': this.state.mapIsLoading
                            })} style={{width: "100%", height: '100vh',  maxHeight: "583px", minHeight: "583px"}}>
                                <GoogleMapReact
                                    bootstrapURLKeys={{
                                        key: GOOGLE_MAPS_API_KEY,                                            
                                        language: 'sv' 
                                    }}
                                    defaultZoom={6}
                                    defaultCenter={center}
                                    yesIWantToUseGoogleMapApiInternals={true}
                                    onGoogleApiLoaded={({ map, maps }) => this.handleApiLoaded(map, maps)}>
                                </GoogleMapReact>
                                <SpinnerDots />
                            </div>

                            <FilteredChains {...details} type={this.props.type} />


                            <div className="details__container">
                                <div className="details__container-inner">
                                    <Demographics demographics={details.scbReportData.populationByGenderDataset} 
                                                  heading="Demografi" />
                                </div>
                            </div>
                            <div className="details__container">
                                <div className="details__container-inner">

                                    <div className="details__split-col-wrapper">
                                        <div className="details__split-col">
                                            <div className="details__col">
                                                <Population data={details.scbReportData.populationByAgeDataset} />
                                            </div>
                                            <div className="details__col">
                                                <PurchasingPower data={details.scbReportData.populationByEconomicStandardDataset} />
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div className="details__split-col-wrapper details__split-col-wrapper--print">
                                        <div className="details__split-col">
                                            <div className="details__col">
                                                <Population data={details.scbReportData.populationByAgeDataset} />
                                            </div>
                                            <div className="details__col">
                                                <PurchasingPower data={details.scbReportData.populationByEconomicStandardDataset} />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="details__split-col-wrapper details__split-col-wrapper--print">
                                        <div className="details__split-col">
                                            <div className="details__col">
                                                <Origin data={details.scbReportData.populationByOriginDataset} />
                                            </div>
                                            <div className="details__col">
                                                <Household data={details.scbReportData.houseHoldTypesDataset} />
                                            </div>
                                        </div>
                                    </div>

                                    <div className="details__split-col-wrapper">
                                        <div className="details__split-col">
                                            <div className="details__col">
                                                <Origin data={details.scbReportData.populationByOriginDataset} />
                                            </div>
                                            <div className="details__col">
                                                <Household data={details.scbReportData.houseHoldTypesDataset} />
                                            </div>
                                        </div>
                                    </div>

                                </div>
                            </div>
                            {(this.props.type !== 'foodservice' && details && details.filterSelection.classifications.length > 0) &&
                                <div className="details__classifications"> 
                                    <div className="details__container">
                                        <div className="details__container-inner details__container-inner--no-padding">
                                            <h2 className="details__classifications-heading">Klassificering</h2>
                                            <ClassificationsTable
                                                rows={details.filterSelection.classifications}
                                                columns={this.getClassificationTableColumns()}
                                                footer={this.getClassificationTableFooter(details.filterSelection.classifications)}
                                            />
                                        </div>
                                    </div>
                                </div>
                            }
                        </>
                    }
                    <ExportFooter print={() => window.print()} 
                                  export={() => this.export()} 
                                  {...this.state} 
                                  btnComponent={
                                    <DropdownButton 
                                        ref={this.dropDown} 
                                        controls="export-dropdown" 
                                        buttonModifiers={{
                                            'export-footer__button': true
                                        }}
                                        buttonChildren={<><div className="export-footer__download-icon"><ButtonAnimation/></div>{this.getExportStateText()}</>}
                                        text="Exportera"
                                        modifiers={{
                                            'export-footer__dropdown-button': true
                                        }}>
                                        <li>
                                            <button type="button" onClick={() => this.export('excel')}>Excel</button>    
                                        </li>
                                        <li>
                                            <button type="button" onClick={() => this.export('xml')}>XML</button>
                                        </li>
                                    </DropdownButton>}>
                    </ExportFooter>
                </div>
                
                <Spinner isLoading={this.state.isLoading} fast={true} />
            </section>
        );
    }
}

export default withAuthProvider(withFetch(withRouter(Details)));